import { useGetMe } from 'api-hooks/auth/query';
import { GuestModel } from 'api-hooks/guest';
import {
  TravelDocumentSalesModel,
  TravelDocumentSalesMutationInput,
} from 'api-hooks/sale/travel-document';
import { Notification } from 'baseui/notification';
import { AlertSVG, PlusCircleSVG } from 'common/assets';
import { AuthorizationRules } from 'common/constants';
import formSetErrors from 'common/helpers/form-setError';
import notification from 'components/common/notification';
import { Form, FormState } from 'components/elements';
import { Button } from 'components/elements/buttons';
import FormHeader from 'components/widgets/form-header';
import { useAuthorization } from 'hooks/use-authorization';
import TitleStatus from 'legacy-modules/common/title-status';
import useCreateGuestAction from 'legacy-modules/master/guest/hooks/use-create-guest-action';
import SalePurchaseRefundTable from 'legacy-modules/refund/components/sale-purchase-table';
import ParentSaleRelatedTable from 'legacy-modules/sales/component/parent-children-sales-table';
import { guestNameTransformer } from 'legacy-modules/sales/ticket-sale/hooks/use-get-default-values';
import {
  HalfContainer,
  SectionContainer,
  SeparatorHorizontal,
  SeparatorVertical,
} from 'legacy-modules/styles';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFieldArray, useForm } from 'react-hook-form';

import PurchaseRelatedTable from '../../component/purchase-related-table';
import ActionComponent from '../components/action-component';
import CostField from '../components/cost-field';
import DocumentDetailField from '../components/document-detail-field';
import {
  DocumentSaleFormType,
  DocumentSaleGuestTypeFormType,
  DocumentSalesFormType,
} from '../components/form-type';
import InformationField from '../components/information-field';
import TravelDocumentInvoiceSelectInput from '../components/invoice-select-input';
import SalesDetailField from '../components/sales-detail-field';
import { FormContent, FormDivider } from '../components/styles';
import BottomSection from '../components/summary-price/bottom-section';
import useCheckCompletenessDocument from '../hooks/use-check-completeness-document';
import useGetDefaultValues, {
  addNewDocumentSale,
  addNewGuest,
} from '../hooks/use-get-default-values';

interface Props {
  travelDocument?: TravelDocumentSalesModel;
  onSubmit: (
    input: TravelDocumentSalesMutationInput,
    form: ReturnType<typeof useForm>,
  ) => Promise<TravelDocumentSalesModel | undefined>;
  renderError?: React.ReactNode;
  defaultValues?: DocumentSaleFormType;
}

export default function TravelDocumentInvoiceForm(props: Props) {
  const { travelDocument } = props;

  const { t } = useTranslation();
  const { can } = useAuthorization();
  const { asPath } = useRouter();

  const defaultValues = useGetDefaultValues({
    travelDocument,
    defaultValues: props?.defaultValues,
  });

  const { data: employeeData } = useGetMe();
  const employee = employeeData?.data;

  const methods = useForm({
    defaultValues,
    mode: 'all',
  });

  const { fields, append, remove } = useFieldArray({
    control: methods.control,
    name: 'documentSales',
    keyName: 'customId',
  });

  const onAddDocument = React.useCallback(() => {
    append(addNewDocumentSale());
  }, [append]);

  const onRemoveDocument = React.useCallback(
    (index) => remove(index),
    [remove],
  );

  const onSubmit = React.useCallback(
    async (values: DocumentSaleFormType) => {
      try {
        const { tempTransactionAt, travelDocument, ...rest } = values;

        const needApproval =
          tempTransactionAt?.getTime() !== values?.transactionAt?.getTime();

        const documentSales = values.documentSales.map((ds) => {
          return {
            ...ds,
            visaDocuments: ds.visaDocuments.map((vd) => vd.id),
            documentSaleGuests: ds.documentSaleGuests.map((dsg) => {
              return {
                ...dsg,
                preferences: dsg.preferences.map((dsgp) => dsgp.id),
              };
            }),
          };
        });

        const result = {
          ...rest,
          needApproval,
          documentSales,
        };
        await props.onSubmit(result, methods as any);
      } catch (e) {
        if (e.errors) {
          formSetErrors(e.errors, methods.setError);
        }
        notification.error({ message: e.message });
      }
    },
    [methods, props],
  );

  const isCompleted = useCheckCompletenessDocument(travelDocument);
  const [showSnack, setShowSnack] = React.useState(true); // TODO: LATER CHANGE TO FALSE

  const isContribute =
    travelDocument?.admin.id === employee?.id ||
    travelDocument?.salesman?.id === employee?.id ||
    travelDocument?.operator?.id === employee?.id;

  const core = can(AuthorizationRules.SalesCore);

  const onRenderComponent = travelDocument && (isContribute || core) && (
    <>
      <ActionComponent data={travelDocument} defaultValues={defaultValues} />
    </>
  );

  const customViewName =
    travelDocument &&
    `${t('common:travel_document_sales')} - ${travelDocument.invoiceNumber}`;

  const { getValues, setValue } = methods;
  const onSuccessCreatGuest = React.useCallback(
    (guest: GuestModel) => {
      const guestName = guestNameTransformer(guest);
      // const guestPreferences = guestPreferencesTransformer(guest);
      const documentSales: DocumentSalesFormType[] = getValues(
        'documentSales',
      ).map((ds) => {
        const documentSaleGuests: DocumentSaleGuestTypeFormType[] = [
          ...ds.documentSaleGuests,
          {
            ...addNewGuest(),
            guestName,
            guestId: guest.id,
          },
        ];
        return {
          ...ds,
          documentSaleGuests,
        };
      });
      setValue('documentSales', documentSales);
    },
    [getValues, setValue],
  );

  const { createGuestComponent } = useCreateGuestAction({
    onSuccess: onSuccessCreatGuest,
  });

  const onRelatedInvoiceAfterChange = React.useCallback(
    (value: TravelDocumentSalesModel) => {
      setValue('parentRefId', value.refId);
      setValue('operatorId', value.operator?.id || '');
      setValue('salesmanId', value.salesman?.id || '');
      setValue('documentAdminId', value.documentAdmin?.id || '');
      setValue('customerId', value.customer?.id || '');
      setValue('customerBillingId', value.customerBillingAddress?.id || '');
    },
    [setValue],
  );

  return (
    <Form
      methods={methods}
      onSubmit={onSubmit}
      defaultEditable={!travelDocument}
      style={style.form}
      customViewName={customViewName}
      keyName={asPath}
    >
      <FormContent>
        {showSnack && travelDocument && !isCompleted && (
          <Notification
            closeable
            kind="info"
            onClose={() => setShowSnack(false)}
            overrides={{
              Body: {
                style: style.notification,
              },
            }}
          >
            {() => (
              <div style={style.notificationContent}>
                <AlertSVG size={16} />
                <SeparatorHorizontal $margin={16} />
                <span>{t('ticketSales:input_document')}</span>
              </div>
            )}
          </Notification>
        )}
        <SeparatorVertical $margin={16} />
        <FormState>
          {({ editable, setIsEditable }) => (
            <FormHeader
              data={travelDocument}
              title={
                travelDocument ? (
                  <TitleStatus
                    type="sale-document"
                    isCompleted={travelDocument.isCompleted}
                    status={travelDocument.documentSaleStatus}
                    title={customViewName ?? t('common:travel_document_sales')}
                  />
                ) : (
                  t('common:add_extra', {
                    extra: t('common:travel_document_sales'),
                  })
                )
              }
              noSubmit
              noEdit
              noDelete
              onRenderComponent={
                editable ? createGuestComponent : onRenderComponent
              }
            />
          )}
        </FormState>
        {props.renderError}
        <div>
          {travelDocument && !!(travelDocument?.releaseSales || []).length && (
            <SalePurchaseRefundTable data={travelDocument.releaseSales} />
          )}
          {!!travelDocument?.purchases.length && (isContribute || core) && (
            <PurchaseRelatedTable
              data={travelDocument.purchases}
              navigation="DocumentPurchasesView"
            />
          )}
          {travelDocument &&
            (!!travelDocument.parentSale ||
              !!(travelDocument.childrenSales || []).length) && (
              <ParentSaleRelatedTable
                data={travelDocument}
                navigation="TravelDocumentSalesView"
              />
            )}
          <SectionContainer>
            <HalfContainer>
              <TravelDocumentInvoiceSelectInput
                label={t('common:related_invoice')}
                placeholder={t('common:choose_invoice')}
                name="parentRefId"
                onAfterDetailChange={onRelatedInvoiceAfterChange}
                isSales
              />
            </HalfContainer>
          </SectionContainer>
          <InformationField />
          <SalesDetailField />
          <SeparatorVertical $margin={36} />

          {fields.map((field, idx) => (
            <React.Fragment key={field.customId}>
              <DocumentDetailField
                parentIndex={idx}
                onRemove={() => onRemoveDocument(idx)}
                fieldLength={fields.length}
              />
              <SeparatorVertical $margin={36} />
            </React.Fragment>
          ))}

          <FormState>
            {({ editable }) =>
              editable && (
                <>
                  <SeparatorHorizontal $margin={16} />
                  <Button
                    startEnhancer={(color, size) => (
                      <PlusCircleSVG color={color} size={size} />
                    )}
                    buttonType="secondary"
                    onClick={onAddDocument}
                  >
                    {t('common:add')}
                  </Button>
                </>
              )
            }
          </FormState>
          <SeparatorVertical $margin={36} />
          <CostField />
          <FormDivider />
        </div>
      </FormContent>
      <BottomSection documentSales={travelDocument} />
    </Form>
  );
}

const style: { [key: string]: React.CSSProperties } = {
  form: { display: 'flex', flexDirection: 'column', height: '100%' },
  notification: {
    margin: '0',
    alignItems: 'center',
    width: '100%',
  },
  notificationContent: { display: 'flex', alignItems: 'center' },
};
